/* eslint-disable fp/no-mutation */
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import React, { ReactElement, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from '@tanstack/react-form'
import * as z from 'zod'
import axios from 'src/utilities/http-client'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  fetchAccreditations,
  selectAccreditationsByCourseID,
  selectAccreditationsStatus,
} from 'src/v3/create/create.slice'
import {
  AddIcon,
  DeleteForeverIcon,
  EditIcon,
  LeftArrowIcon,
  RightArrowIcon,
} from 'src/v3/core/icons'
import { AccreditationType, Course } from 'src/v3/core/types'
import ConfirmAction from 'src/v3/components/confirm-action'
import useSession from 'src/authentication/hooks/use-session'

interface Props {
  course?: Course
  setStep: React.Dispatch<
    React.SetStateAction<
      'basic-info' | 'classes' | 'accreditations' | 'publishing'
    >
  >
}

let accreditationTypes: AccreditationType[] = [
  'notAccredited',
  'accredited',
  'capce',
  'ceBroker',
  'newJersey',
  'newYork',
  'virginia',
]

let accreditationLabels: Record<AccreditationType, string> = {
  notAccredited: 'Not Accredited',
  accredited: 'Accredited',
  capce: 'CAPCE',
  ceBroker: 'CE Broker',
  newJersey: 'New Jersey',
  newYork: 'New York',
  virginia: 'Virginia',
}

export default ({ course, setStep }: Props): ReactElement => {
  let dispatch = useAppDispatch()
  let navigate = useNavigate()
  let session = useSession()

  let accreditations = useAppSelector(
    selectAccreditationsByCourseID(course?.id || ''),
  )
  let accreditationsStatus = useAppSelector(selectAccreditationsStatus)

  let [errorMessage, setErrorMessage] = useState<string>('')
  let [saving, setSaving] = useState<boolean>(false)
  let [deleting, setDeleting] = useState<string>('')
  let [formOpen, setFormOpen] = useState<boolean>(false)

  let isAdmin = session?.roles.includes('admin')
  let isLinkedServiceCourse = !!course?.linkedServiceID

  let validationSchema = z.object({
    accreditationType: z.string().refine((val) => val !== 'unselected', {
      message: 'Accreditation Type must be selected',
    }),
    courseCode: z.string(),
    topic: z.string(),
    subTopic: z.string(),
    topicArea: z.string(),
    areaNumberALS: z.string(),
    areaNumberBLS: z.string(),
    professionCode: z.string(),
    subjectArea: z.string(),
    state: z.string(),
    id: z.string(),
  })

  let form = useForm({
    defaultValues: {
      id: '',
      accreditationType: 'unselected',
      courseCode: '',
      topic: '',
      subTopic: '',
      topicArea: '',
      areaNumberALS: '',
      areaNumberBLS: '',
      professionCode: '',
      subjectArea: '',
      state: '',
    },
    validators: {
      onChange: validationSchema,
    },
    onSubmit: async ({ value: values }) => {
      if (values.accreditationType === 'unselected') {
        setErrorMessage('Accreditation Type must be selected')
        return
      }

      if (!course) {
        setErrorMessage('Course must be created first')
        return
      }

      setSaving(true)

      try {
        let baseAccreditation: Record<string, any> = {
          courseID: course.id,
          courseCode: values.courseCode.trim(),
          accreditationType: values.accreditationType,
        }

        if (values.accreditationType === 'virginia') {
          baseAccreditation = {
            courseID: course.id,
            topic: values.topic.trim(),
            subTopic: values.subTopic.trim(),
            areaNumberALS: values.areaNumberALS.trim(),
            areaNumberBLS: values.areaNumberBLS.trim(),
          }
        } else if (values.accreditationType === 'ceBroker') {
          baseAccreditation = {
            courseID: course.id,
            courseCode: values.courseCode.trim(),
            professionCode: values.professionCode.trim(),
            subjectArea: values.subjectArea.trim(),
            state: values.state,
          }
        } else if (values.accreditationType === 'newYork') {
          baseAccreditation = {
            courseID: course.id,
            topicArea: values.topicArea,
          }
        }

        if (!values.id) {
          await axios.post('/api/v2/create/accreditation', {
            [values.accreditationType]: baseAccreditation,
          })
        } else {
          await axios.put('/api/v2/create/accreditation', {
            [values.accreditationType]: {
              id: values.id,
              ...baseAccreditation,
            },
          })
        }

        // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
        await dispatch(fetchAccreditations())
        setFormOpen(false)
        form.reset()
      } catch (err: any) {
        setErrorMessage(
          err?.response?.data || err?.message || 'Error saving accreditation',
        )
      } finally {
        setSaving(false)
      }
    },
  })

  useEffect(() => {
    if (accreditationsStatus === 'uninitialized') {
      dispatch(fetchAccreditations())
    }
  }, [accreditationsStatus])

  useEffect(() => {
    if (!formOpen) {
      form.reset()
    }
  }, [formOpen])

  let hasAccreditation = (accreditationType: AccreditationType) =>
    accreditations.some(
      (a) =>
        a[accreditationType]?.courseID === course?.id &&
        (!form.getFieldValue('id') ||
          a[accreditationType]?.id !== form.getFieldValue('id')),
    )

  let onEdit = (id: string) => {
    let accreditation = accreditations.find((a) => {
      if (a.capce) return a.capce.id === id
      if (a.newJersey) return a.newJersey.id === id
      if (a.newYork) return a.newYork.id === id
      if (a.virginia) return a.virginia.id === id
      if (a.ceBroker) return a.ceBroker.id === id
      if (a.accredited) return a.accredited.id === id
      if (a.notAccredited) return a.notAccredited.id === id
      return false
    })

    if (!accreditation) return

    let data: any = {}
    let accType = ''

    if (accreditation.capce) {
      data = accreditation.capce
      accType = 'capce'
    } else if (accreditation.newJersey) {
      data = accreditation.newJersey
      accType = 'newJersey'
    } else if (accreditation.newYork) {
      data = accreditation.newYork
      accType = 'newYork'
    } else if (accreditation.virginia) {
      data = accreditation.virginia
      accType = 'virginia'
    } else if (accreditation.ceBroker) {
      data = accreditation.ceBroker
      accType = 'ceBroker'
    } else if (accreditation.accredited) {
      data = accreditation.accredited
      accType = 'accredited'
    } else if (accreditation.notAccredited) {
      data = accreditation.notAccredited
      accType = 'notAccredited'
    }

    form.setFieldValue('id', data.id)
    form.setFieldValue('accreditationType', accType)
    form.setFieldValue('courseCode', data.courseCode || '')
    form.setFieldValue('topic', data.topic || '')
    form.setFieldValue('subTopic', data.subTopic || '')
    form.setFieldValue('topicArea', data.topicArea || '')
    form.setFieldValue('areaNumberALS', data.areaNumberALS || '')
    form.setFieldValue('areaNumberBLS', data.areaNumberBLS || '')
    form.setFieldValue('professionCode', data.professionCode || '')
    form.setFieldValue('subjectArea', data.subjectArea || '')
    form.setFieldValue('state', data.state || '')
    setFormOpen(true)
  }

  let onDelete = async (id: string) => {
    try {
      await axios.delete(
        `/api/v2/create/delete-accreditation?accreditationID=${id}`,
      )
      // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
      await dispatch(fetchAccreditations())
    } catch (err: any) {
      setErrorMessage(err?.message || 'Error deleting accreditation')
    }
  }

  let renderAccreditationFields = () => {
    let accType = form.getFieldValue('accreditationType')

    if (accType === 'virginia') {
      return (
        <>
          <form.Field name="topic">
            {(field) => (
              <TextField
                fullWidth
                className="mt-4"
                variant="standard"
                value={field.state.value}
                label="Topic"
                name={field.name}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
              />
            )}
          </form.Field>
          <form.Field name="subTopic">
            {(field) => (
              <TextField
                fullWidth
                className="mt-4"
                variant="standard"
                value={field.state.value}
                label="Sub Topic"
                name={field.name}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
              />
            )}
          </form.Field>
          <form.Field name="areaNumberALS">
            {(field) => (
              <TextField
                fullWidth
                className="mt-4"
                variant="standard"
                value={field.state.value}
                label="Area Number ALS"
                name={field.name}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
              />
            )}
          </form.Field>
          <form.Field name="areaNumberBLS">
            {(field) => (
              <TextField
                fullWidth
                className="mt-4"
                variant="standard"
                value={field.state.value}
                label="Area Number BLS"
                name={field.name}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
              />
            )}
          </form.Field>
        </>
      )
    }

    if (accType === 'ceBroker') {
      return (
        <>
          <form.Field name="courseCode">
            {(field) => (
              <TextField
                fullWidth
                className="mt-4"
                variant="standard"
                value={field.state.value}
                label="Course Code"
                name={field.name}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
              />
            )}
          </form.Field>
          <form.Field name="professionCode">
            {(field) => (
              <TextField
                fullWidth
                className="mt-4"
                variant="standard"
                value={field.state.value}
                label="Profession Code"
                name={field.name}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
              />
            )}
          </form.Field>
          <form.Field name="subjectArea">
            {(field) => (
              <TextField
                fullWidth
                className="mt-4"
                variant="standard"
                value={field.state.value}
                label="Subject Area"
                name={field.name}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
              />
            )}
          </form.Field>
          <form.Field name="state">
            {(field) => (
              <FormControl fullWidth className="mt-4">
                <InputLabel>State</InputLabel>
                <Select
                  variant="standard"
                  value={field.state.value}
                  label="State"
                  name={field.name}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                >
                  <MenuItem value="Florida">Florida</MenuItem>
                  <MenuItem value="Kentucky">Kentucky</MenuItem>
                  <MenuItem value="Tennessee">Tennessee</MenuItem>
                </Select>
              </FormControl>
            )}
          </form.Field>
        </>
      )
    }

    if (accType === 'newYork') {
      return (
        <form.Field name="topicArea">
          {(field) => (
            <FormControl fullWidth className="mt-4">
              <InputLabel>Topic Area</InputLabel>
              <Select
                variant="standard"
                value={field.state.value}
                label="Topic Area"
                name={field.name}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
              >
                <MenuItem value="Cardiovascular Emergencies">
                  Cardiovascular Emergencies
                </MenuItem>
                <MenuItem value="Trauma">Trauma</MenuItem>
                <MenuItem value="Medical">Medical</MenuItem>
                <MenuItem value="Pediatrics">Pediatrics</MenuItem>
                <MenuItem value="Operations">Operations</MenuItem>
              </Select>
            </FormControl>
          )}
        </form.Field>
      )
    }

    // Default: courseCode field for capce, notAccredited, etc.
    return (
      <form.Field name="courseCode">
        {(field) => (
          <TextField
            fullWidth
            className="mt-4"
            variant="standard"
            value={field.state.value}
            label="Course Code"
            name={field.name}
            onChange={(e) => field.handleChange(e.target.value)}
            onBlur={field.handleBlur}
          />
        )}
      </form.Field>
    )
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ mt: 4, mb: 2 }}>
        Course Accreditations
      </Typography>
      {!course && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Please complete the Basic Info step first to manage accreditations.
        </Alert>
      )}
      {course && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Add accreditations for this course. At least one is required to
            publish.
          </Typography>

          <Box sx={{ mb: 3 }}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setFormOpen(true)}
              disabled={!course}
            >
              Add Accreditation
            </Button>
          </Box>

          <Box className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {accreditations
              .filter(
                (a) =>
                  a.capce?.courseID === course.id ||
                  a.ceBroker?.courseID === course.id ||
                  a.newJersey?.courseID === course.id ||
                  a.newYork?.courseID === course.id ||
                  a.virginia?.courseID === course.id ||
                  a.accredited?.courseID === course.id ||
                  a.notAccredited?.courseID === course.id,
              )
              .map((a) => {
                let accredData: any = null
                let accredType = ''
                let accredId = ''

                if (a.capce) {
                  accredData = a.capce
                  accredType = 'capce'
                  accredId = a.capce.id
                } else if (a.ceBroker) {
                  accredData = a.ceBroker
                  accredType = 'ceBroker'
                  accredId = a.ceBroker.id
                } else if (a.newJersey) {
                  accredData = a.newJersey
                  accredType = 'newJersey'
                  accredId = a.newJersey.id
                } else if (a.newYork) {
                  accredData = a.newYork
                  accredType = 'newYork'
                  accredId = a.newYork.id
                } else if (a.virginia) {
                  accredData = a.virginia
                  accredType = 'virginia'
                  accredId = a.virginia.id
                } else if (a.accredited) {
                  accredData = a.accredited
                  accredType = 'accredited'
                  accredId = a.accredited.id
                } else if (a.notAccredited) {
                  accredData = a.notAccredited
                  accredType = 'notAccredited'
                  accredId = a.notAccredited.id
                }

                return (
                  <Box>
                    <Card variant="outlined">
                      <CardContent>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            mb: 2,
                          }}
                        >
                          <Chip
                            label={
                              accreditationLabels[
                                accredType as AccreditationType
                              ]
                            }
                            color="primary"
                            variant="outlined"
                          />
                          <Box>
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => onEdit(accredId)}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() =>
                                  setDeleting(`delete-${accredId}`)
                                }
                              >
                                <DeleteForeverIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>

                        <Typography variant="body2" color="text.secondary">
                          {accredData?.courseCode &&
                            `Code: ${accredData.courseCode}`}
                          {accredData?.topic && `Topic: ${accredData.topic}`}
                          {accredData?.topicArea &&
                            `Area: ${accredData.topicArea}`}
                          {accredData?.state && `State: ${accredData.state}`}
                        </Typography>
                      </CardContent>
                    </Card>

                    <ConfirmAction
                      executeAction={() => onDelete(accredId)}
                      open={deleting}
                      setOpen={setDeleting}
                      openText={`delete-${accredId}`}
                      title="Confirm Deletion"
                      text={`Are you sure you want to delete this ${accreditationLabels[accredType as AccreditationType]} accreditation?`}
                      onFailure={(err) =>
                        setErrorMessage(
                          err.message || 'Error deleting accreditation',
                        )
                      }
                    />
                  </Box>
                )
              })}
          </Box>
        </>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<LeftArrowIcon />}
            onClick={() => setStep('classes')}
          >
            Back to Classes
          </Button>
          <Button variant="outlined" onClick={() => navigate('/create/v4')}>
            Save and Exit
          </Button>
        </Box>

        <Button
          variant="contained"
          endIcon={<RightArrowIcon />}
          onClick={() => setStep('publishing')}
        >
          Continue to Publishing
        </Button>
      </Box>
      {/* Add/Edit Accreditation Dialog */}
      <Dialog
        open={formOpen && !saving}
        onClose={() => setFormOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {form.getFieldValue('id') ? 'Edit' : 'Add'} Accreditation
        </DialogTitle>
        <DialogContent>
          <form
            onSubmit={(e) => {
              e.preventDefault()
              e.stopPropagation()
              form.handleSubmit()
            }}
          >
            <form.Field name="accreditationType">
              {(field) => (
                <FormControl fullWidth sx={{ mt: 1 }}>
                  <InputLabel>Accreditation Type</InputLabel>
                  <Select
                    variant="standard"
                    value={field.state.value}
                    label="Accreditation Type"
                    name={field.name}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    error={field.state.meta.errors.length > 0}
                  >
                    <MenuItem value="unselected">Select Type</MenuItem>
                    {accreditationTypes
                      .filter((type) => {
                        if (isLinkedServiceCourse) {
                          return (
                            type === 'accredited' || type === 'notAccredited'
                          )
                        }
                        return (
                          isAdmin &&
                          type !== 'accredited' &&
                          type !== 'notAccredited'
                        )
                      })
                      .filter((type) => !hasAccreditation(type))
                      .map((type) => (
                        <MenuItem key={type} value={type}>
                          {accreditationLabels[type]}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              )}
            </form.Field>

            {form.getFieldValue('accreditationType') !== 'unselected' &&
              renderAccreditationFields()}

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 2,
                mt: 3,
              }}
            >
              <Button variant="outlined" onClick={() => setFormOpen(false)}>
                Cancel
              </Button>
              <Button variant="contained" type="submit" disabled={saving}>
                {saving
                  ? 'Saving...'
                  : form.getFieldValue('id')
                    ? 'Update'
                    : 'Add'}
              </Button>
            </Box>
          </form>
        </DialogContent>
      </Dialog>
      <Snackbar open={!!errorMessage} onClose={() => setErrorMessage('')}>
        <Alert
          variant="filled"
          onClose={() => setErrorMessage('')}
          severity="error"
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  )
}
